'use client'

import { Camera, Check, ChevronDown, X } from 'lucide-react'
import Link from 'next/link'

import { ChoiceChip } from '@/components/molecules/choice-chip.client'
import { OrderLineFields } from '@/components/sections/order/order-line-fields.client'
import { OrderLinePrice } from '@/components/sections/order/order-line-price'
import {
  type CreatorCityView,
  type CreatorOfferingView,
  type CreatorPick,
  defaultQtyFor,
  fieldShown,
  isConfiguredShoot,
  needsRange,
  type PickField,
  rentalTermsOf,
} from '@/lib/creator'
import { formatDay } from '@/lib/dates'
import { lineFacts } from '@/lib/order-line-summary'
import type { LineView } from '@/lib/order-line-view'
import { lastHeldDay } from '@/lib/rental'
import { recordingRateOf, shootScopeOf } from '@/lib/shoot-pricing'
import { formatPLN } from '@/lib/tiers'
import { META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * One line of the order — the only place a line is drawn.
 *
 * There used to be two: this card and a second, smaller one in the basket
 * panel, each with its own stepper and its own discount badge over a different
 * set of lines. They disagreed the moment something was configured but not yet
 * "added". Deleting one of them is what made the merged screen possible; the
 * right-hand column is now arithmetic and nothing else.
 *
 * The completeness gate moved with it — from the BUTTON to the FIELD. There is
 * no "Dodaj do zamówienia" any more, so a line that is missing its date is in
 * the order and says what it needs, instead of being held outside it by a
 * disabled button that never explains itself.
 *
 * ── AND IT CLOSES ────────────────────────────────────────────────────────
 * Every position used to be a permanently open form. Five of them measured
 * 3 483 px of list inside a 4 811 px page — 5.3 screens at 1440×900 — so there
 * was no zoom level at which a person could see their own order. A closed card
 * is a header, its facts and its price: enough to recognise the position, and
 * nothing that has to be scrolled past to reach the next one.
 *
 * What a closed card must never hide is a problem, so the status sits in the
 * header at both sizes and names the field that is missing. The list opens the
 * first unfinished position for you — see `OrderLineList`.
 */
export function OrderLineCard({
  view,
  cities,
  films,
  open,
  onToggle,
  onPatch,
  onDrop,
}: {
  view: Extract<LineView, { status: 'draft' | 'ready' }>
  cities: CreatorCityView[]
  /** The other films in this order, for "rolki z tego filmu" (spec §8 · G8). */
  films: { lineId: string; label: string }[]
  open: boolean
  onToggle: () => void
  onPatch: (next: Partial<CreatorPick>) => void
  onDrop: () => void
}) {
  const { line, offering, variant } = view
  const bodyId = `pozycja-${line.lineId}`
  const facts = lineFacts(offering, line.pick, variant?.name, cities)
  const problemField = view.status === 'ready' ? null : view.field
  const problem = view.status === 'ready' ? null : view.problem

  /** The refusal belongs on the variant row rather than on a field below it. */
  const onVariants =
    problem !== null &&
    (problemField === 'variant' || (problemField === 'length' && needsRange(offering)))
  /**
   * Nothing may go unsaid. A field that is not drawn cannot carry its own
   * complaint, so whatever is left over is printed at the foot of the card —
   * which is where every complaint used to live, kept as the floor rather than
   * as the rule.
   */
  const orphanProblem =
    problem !== null &&
    !onVariants &&
    (problemField === null || !fieldShown(offering, line.pick, problemField))
      ? problem
      : null

  /**
   * A hire's term IS its variant (spec §11 · I4), so switching the chip moves
   * the return date. Rewritten here rather than left stale: the stored `to` is
   * what `cart_item.to_day` carries, and a row that says the 3rd while the
   * order will be billed to the 7th is a row nobody can audit.
   */
  /**
   * „Sesja rodzinna · 4 osoby · 50 zdjęć", or `null` when the line is not one.
   *
   * Read off the offering's own rate card rather than off the pick, so the words
   * are the ones the owner typed into the panel — „Od 101 do 150 m²", not
   * `101-150`. A scope the card no longer sells still prints its slug rather
   * than vanishing: the customer bought it and the line has to keep saying so.
   */
  const shoot = shootSummary(offering, line.pick)

  const pickVariant = (slug: string, qty: number): Partial<CreatorPick> => {
    if (!needsRange(offering)) return { variantSlug: slug, qty }
    const days = rentalTermsOf(offering).find((term) => term.variantSlug === slug)?.days ?? 0
    return { variantSlug: slug, qty, to: lastHeldDay(line.pick.from, days) }
  }

  return (
    <section
      aria-label={offering.name}
      className={cn(
        'rounded-3xl border backdrop-blur-md transition-colors duration-300',
        problem
          ? 'border-[oklch(0.8_0.15_75_/_0.35)] bg-white/[0.03]'
          : variant
            ? 'border-white/25 bg-white/[0.06]'
            : 'border-white/[0.12] bg-white/[0.03]',
      )}
    >
      {/* The header is a row, not a button wrapping a row: it carries a link
          and a delete control, and neither may be nested inside a toggle.

          Three lines closed — what it is, then what was answered and whether
          that is enough, with the amount on the right of the first one. It was
          four until the price came out of the status row: a summary that runs
          taller than five of them fit on a screen is not a summary. */}
      <div className="flex items-start gap-2 p-4 sm:p-5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="-m-2 flex min-w-0 flex-1 items-start gap-3 rounded-2xl p-2 text-left transition hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none"
        >
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={cn(
              'mt-0.5 shrink-0 text-white/45 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-3">
              <span className={cn(META, 'font-semibold tracking-[0.14em] text-white/55 uppercase')}>
                {offering.pageLabel}
              </span>
              {view.money && view.money.total > 0 ? (
                <span className="shrink-0 text-[0.95rem] font-semibold text-white">
                  {formatPLN(view.money.total)}
                  {view.money.quoteOnly ? (
                    <span className={cn(MICRO, 'font-medium text-white/55')}> szac.</span>
                  ) : null}
                </span>
              ) : null}
            </span>
            <span className="mt-0.5 block text-[1.05rem] font-bold text-white">
              {offering.name}
            </span>

            <span className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              <StatusPill problem={problem} field={problemField} />
              {/* Open: the blurb, because the fields below need the context.
                  Closed: the answers, because the fields are gone. */}
              {open ? (
                offering.blurb ? (
                  <span className={cn(META, 'text-white/55')}>{offering.blurb}</span>
                ) : null
              ) : (
                facts.map((fact, index) => (
                  <span key={fact} className={cn(META, 'flex items-center gap-2.5 text-white/70')}>
                    {fact}
                    {/* Trailing, not leading. A separator in front of the fact
                        wraps WITH it, so a narrow card began a line with "·". */}
                    {index < facts.length - 1 ? <span className="text-white/25">·</span> : null}
                  </span>
                ))
              )}
            </span>
          </span>
        </button>

        <span className="flex shrink-0 items-center gap-1">
          <Link
            href={offering.href}
            className={cn(
              MICRO,
              'hidden font-semibold text-white/55 underline-offset-4 transition hover:text-white hover:underline sm:inline',
            )}
          >
            Zobacz zakres
          </Link>
          {/* 44 px, not the 26 px this control used to be. It is the one
              destructive action on the card and it was the smallest target on
              the page. */}
          <button
            type="button"
            onClick={onDrop}
            data-line-remove=""
            aria-label={`Usuń pozycję: ${offering.name}`}
            className="grid min-h-11 min-w-11 place-items-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </span>
      </div>

      <div id={bodyId} hidden={!open} className="px-4 pb-4 sm:px-5 sm:pb-5">
        {/* ── A CONFIGURED SHOOT SAYS WHAT IT IS, AND IS NOT RE-CONFIGURED HERE ──
            The session, the head count and the photo count were answered
            together on `/pl/fotograf-operator`, and they only make sense
            together: the owner's table sells „4 osoby" with 35, 50 or 70
            photos, and a different session sells a different ladder. Re-asking
            one of them here would let somebody land on a count their session
            does not sell, and the line would quietly fall back to the day rate
            — the exact bug this whole change closes. The operator's „co
            nagrywamy" is stated here for the same reason.

            So the card STATES the configuration and sends them back to the page
            that asked for it. Everything else on the line — the day, the city —
            is still edited here, because none of it changes the price. */}
        {shoot ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
            <Camera size={14} aria-hidden="true" className="shrink-0 text-white/55" />
            <span className="text-[0.85rem] font-semibold text-white">{shoot}</span>
            <Link
              href="/pl/operator"
              className={cn(
                MICRO,
                'ml-auto font-semibold text-white/55 underline hover:text-white',
              )}
            >
              Zmień konfigurację
            </Link>
          </div>
        ) : null}

        {/* Variant chips — clicking the chosen one clears it. NOT RENDERED at
            all for a configured shoot, rather than hidden: a control that is
            only invisible is still in the document, and the next reader of this
            card would have to work out why it is there. */}
        <div className="flex flex-wrap gap-2">
          {(shoot ? [] : offering.variants).map((v) => {
            const isOn = line.pick.variantSlug === v.slug
            return (
              <ChoiceChip
                key={v.slug}
                pressed={isOn}
                // The quantity follows the block: stepping from "godzina" to
                // "cały dzień" must not leave two whole days in the room behind.
                onClick={() =>
                  onPatch(
                    isOn ? { variantSlug: null } : pickVariant(v.slug, defaultQtyFor(offering, v)),
                  )
                }
                note={`${v.price}${v.suffix ? ` ${v.suffix}` : ''}`}
              >
                {v.name}
              </ChoiceChip>
            )
          })}
        </div>

        {/* On the chips, not at the foot of the card. A hire lands here too: its
            term IS its variant (spec §11 · I4), so `pricingIssue` calls the
            missing term a `'length'` and there is no length slider to put it on. */}
        {onVariants ? (
          <p className={cn(META, 'mt-2 font-medium text-[oklch(0.85_0.15_75)]')}>{problem}</p>
        ) : null}

        {/* "Najwcześniejsza data gotowości" (spec §5 · G1), beside the level it
            belongs to — every level has its own performers and its own calendar,
            so Clean and Advanced genuinely answer differently. Read with the 18:00
            cutoff already applied, so this is the date the customer would really
            get rather than an optimistic "jutro".

            Only for a level that HAS a calendar: `readyFrom` is absent for a hire
            (the customer picks the dates) and for anything awaiting a quote. */}
        {variant && variant.readyFrom !== undefined ? (
          <p className={cn(META, 'mt-3 text-white/55')}>
            {variant.readyFrom
              ? `Najwcześniejszy start: ${formatDay(variant.readyFrom)}`
              : 'Ten poziom jest chwilowo wysprzedany — wybierz inny albo napisz do nas.'}
          </p>
        ) : null}

        {variant ? (
          <OrderLineFields
            offering={offering}
            pick={line.pick}
            cities={cities}
            films={films}
            lineId={line.lineId}
            problemField={problemField}
            problem={problem}
            onPatch={onPatch}
          />
        ) : null}

        {/* The price appears the moment there IS one — a chosen variant and a
            quantity — not when the whole line is orderable. The summary beside
            this card adds up exactly these figures. */}
        {/* `money` is non-null only when `pricingProblem` cleared, which needs a
            variant — the `variant &&` is what narrows the type, not a second
            opinion about whether there is one. */}
        {view.money && variant ? (
          <OrderLinePrice offering={offering} pick={line.pick} money={view.money} />
        ) : null}

        {orphanProblem ? (
          <p className={cn(META, 'mt-4 font-medium text-[oklch(0.85_0.15_75)]')}>{orphanProblem}</p>
        ) : null}
      </div>
    </section>
  )
}

/**
 * Ready, or the name of the one thing that is not.
 *
 * The short name rather than the sentence: a header has room for two words, and
 * the sentence is printed in full on the field itself once the card is open.
 * Between them they answer "what do I still have to do" at both sizes, which is
 * what a single amber line at the bottom of a 700 px card never did.
 */
function StatusPill({ problem, field }: { problem: string | null; field: PickField | null }) {
  if (!problem) {
    return (
      <span
        className={cn(
          MICRO,
          'inline-flex items-center gap-1.5 rounded-full border border-[oklch(0.78_0.17_150_/_0.35)] bg-[oklch(0.78_0.17_150_/_0.12)] px-2.5 py-1 font-bold text-[oklch(0.86_0.17_150)]',
        )}
      >
        <Check size={11} aria-hidden="true" />
        Gotowe
      </span>
    )
  }

  const missing = field ? MISSING[field] : null

  return (
    <span
      className={cn(
        MICRO,
        'inline-flex items-center gap-1.5 rounded-full border border-[oklch(0.8_0.15_75_/_0.4)] bg-[oklch(0.8_0.15_75_/_0.12)] px-2.5 py-1 font-bold text-[oklch(0.87_0.15_75)]',
      )}
    >
      {missing ? `Brakuje: ${missing}` : 'Do uzupełnienia'}
    </span>
  )
}

/** What a header calls each field. Two words at most — see `StatusPill`. */
const MISSING: Record<PickField, string> = {
  variant: 'wariantu',
  qty: 'ilości',
  date: 'terminu',
  time: 'godziny',
  city: 'miasta',
  brief: 'opisu',
  source: 'linku',
  footage: 'długości surówki',
  length: 'długości',
}

/**
 * The one line a configured shoot — or a configured recording — is summarised
 * as. See `shoot` above.
 *
 * The operator's version is one word, because his day is one answer: „Podcast
 * lub wywiad" says everything a photographer needs three fields for.
 */
function shootSummary(offering: CreatorOfferingView, pick: CreatorPick): string | null {
  if (pick.recordingType) {
    const type = recordingRateOf(offering.shoot, pick.recordingType)
    return type?.label ?? pick.recordingType
  }
  if (!isConfiguredShoot(offering, pick)) return null
  const scope = shootScopeOf(offering.shoot, pick.shootSession, pick.shootScope)
  return [
    SESSION_NAMES[pick.shootSession] ?? pick.shootSession,
    scope?.shortLabel ?? pick.shootScope,
    `${pick.shootPhotos} zdj.`,
  ].join(' · ')
}

/**
 * What each session is CALLED, for the basket's one line.
 *
 * Not in the database: `shoot_scope` stores the answers a session offers, not
 * the session's own name — and the name is copy, the same as the question above
 * it on `/pl/fotograf-operator`. A slug with no entry prints as itself, which
 * is ugly and true.
 */
const SESSION_NAMES: Record<string, string> = {
  wizerunkowa: 'Sesja wizerunkowa',
  portretowa: 'Sesja portretowa',
  kobieca: 'Sesja kobieca',
  rodzinna: 'Sesja rodzinna',
  produktowa: 'Sesja produktowa',
  nieruchomosci: 'Nieruchomości',
}
